import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useSchool } from '../contexts/SchoolContext';

// Shown at /admin right after login, before any school is chosen. Picking a
// school here navigates into /admin/:schoolId/orders, making the school
// selection an explicit, visible step baked into the URL rather than
// implicit state.
//
// Exception: a SchoolAdmin with access to exactly one school has no real
// choice to make, so they're forwarded straight to that school's dashboard
// instead of being shown a picker with a single option. FullAdmins always
// see the picker regardless of how many schools exist — they're not scoped
// to any particular school, and the picker is also their jumping-off point
// to the Admin Area link.
function SchoolSelect() {
  const navigate = useNavigate();
  const { logout, user, isFullAdmin } = useAuth();
  const { schools, loading, refetch } = useSchool();
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({ id: '', name: '', grade_options: '' });

  useEffect(() => {
    if (!loading && !isFullAdmin && schools.length === 1) {
      navigate(`/admin/${schools[0].id}/orders`, { replace: true });
    }
  }, [loading, isFullAdmin, schools, navigate]);

  // Minimal inline bootstrap form for the very first school on a fresh
  // install (no schools.yaml, empty database) — there's no :schoolId yet
  // to route into the full Schools.js admin page (that page lives inside
  // the /admin/:schoolId/* shell), so this covers just enough fields to
  // get one school created and unblock everything else. Fuller editing
  // (price, Venmo, teacher settings, etc.) happens in Schools.js afterward.
  const handleCreateFirstSchool = async (e) => {
    e.preventDefault();
    setError('');
    try {
      await axios.post('/api/admin/schools', {
        ...formData,
        grade_options: formData.grade_options.split(',').map(g => g.trim()).filter(Boolean)
      });
      await refetch();
      setCreating(false);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create school');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  // Same loading UI while the auto-forward effect above is about to
  // navigate away — otherwise the single-school picker would flash on
  // screen for one render before the redirect kicks in.
  const autoForwarding = !loading && !isFullAdmin && schools.length === 1;

  if (loading || autoForwarding) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '50vh',
        fontSize: '1.2rem',
        color: '#666'
      }}>
        Loading...
      </div>
    );
  }

  return (
    <div>
      <header className="header no-print">
        <div className="container">
          <h1>🥪 Admin Dashboard</h1>
        </div>
      </header>

      <div className="container" style={{ maxWidth: '600px', margin: '0 auto', paddingTop: '2rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
          <span style={{ color: '#666', fontSize: '0.9rem' }}>
            Welcome, {user?.username}
          </span>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            {isFullAdmin && schools.length > 0 && (
              <Link to="/admin/main/schools" style={{ color: '#007bff', fontSize: '0.9rem', textDecoration: 'none' }}>
                🏢 Admin Area
              </Link>
            )}
            <button
              onClick={handleLogout}
              style={{
                padding: '0.5rem 1rem',
                backgroundColor: '#dc3545',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '0.9rem'
              }}
            >
              Logout
            </button>
          </div>
        </div>

        <h2 style={{ marginBottom: '1rem' }}>Which school?</h2>
        <p style={{ color: '#666', marginBottom: '1.5rem' }}>
          Choose a school to manage its orders, order dates, and teachers.
        </p>

        {schools.length === 0 ? (
          isFullAdmin ? (
            <div style={{ padding: '1.5rem', backgroundColor: 'white', border: '1px solid #dee2e6', borderRadius: '8px' }}>
              <p style={{ color: '#666', marginTop: 0 }}>
                No schools exist yet. Create the first one to get started — you can fill in pricing, Venmo, and other details afterward from the Schools page.
              </p>

              {error && (
                <div style={{ padding: '0.75rem 1rem', backgroundColor: '#f8d7da', color: '#721c24', border: '1px solid #f5c6cb', borderRadius: '4px', marginBottom: '1rem' }}>
                  {error}
                </div>
              )}

              {!creating ? (
                <button
                  onClick={() => setCreating(true)}
                  style={{ padding: '0.75rem 1.5rem', backgroundColor: '#007bff', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                >
                  Create First School
                </button>
              ) : (
                <form onSubmit={handleCreateFirstSchool}>
                  <div style={{ marginBottom: '1rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>School ID (used in URLs and as the subdomain):</label>
                    <input
                      type="text"
                      value={formData.id}
                      onChange={(e) => setFormData(prev => ({ ...prev, id: e.target.value.toLowerCase() }))}
                      required
                      pattern="[a-z0-9-]+"
                      placeholder="lincoln-elem"
                      style={{ width: '100%', padding: '0.5rem', border: '1px solid #ccc', borderRadius: '4px' }}
                    />
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>Name:</label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                      required
                      placeholder="Lincoln Elementary School"
                      style={{ width: '100%', padding: '0.5rem', border: '1px solid #ccc', borderRadius: '4px' }}
                    />
                  </div>
                  <div style={{ marginBottom: '1.5rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>Grade Options (comma-separated):</label>
                    <input
                      type="text"
                      value={formData.grade_options}
                      onChange={(e) => setFormData(prev => ({ ...prev, grade_options: e.target.value }))}
                      required
                      placeholder="K, 1st, 2nd, 3rd, 4th, 5th"
                      style={{ width: '100%', padding: '0.5rem', border: '1px solid #ccc', borderRadius: '4px' }}
                    />
                  </div>
                  <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
                    <button type="button" onClick={() => setCreating(false)} style={{ padding: '0.5rem 1rem', backgroundColor: '#6c757d', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
                      Cancel
                    </button>
                    <button type="submit" style={{ padding: '0.5rem 1rem', backgroundColor: '#28a745', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
                      Create
                    </button>
                  </div>
                </form>
              )}
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>
              No schools have been set up yet. Ask a full administrator to create one.
            </div>
          )
        ) : (
          <div style={{ display: 'grid', gap: '1rem' }}>
            {schools.map(school => (
              <button
                key={school.id}
                onClick={() => navigate(`/admin/${school.id}/orders`)}
                style={{
                  display: 'block',
                  width: '100%',
                  textAlign: 'left',
                  padding: '1.25rem 1.5rem',
                  backgroundColor: 'white',
                  border: '1px solid #dee2e6',
                  borderRadius: '8px',
                  fontSize: '1.1rem',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
                }}
                onMouseOver={(e) => e.currentTarget.style.borderColor = '#007bff'}
                onMouseOut={(e) => e.currentTarget.style.borderColor = '#dee2e6'}
              >
                {school.name}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default SchoolSelect;
